import { useCallback, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/components/layout/Toast';
import authApi from '@/slices/auth/auth.api';
import SymfonyClient from '@/services/SymfonyClient';

const CLIENT_KEY = 'carbon-hub';

export const useCarbonHubSSO = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const isLinkingInProgress = useRef(false);

  const { data: OAuthStatus } = authApi.endpoints.getOAuthStatus.useQuery({
    clientKey: CLIENT_KEY,
  });
  const [fetchOAuthStatus] = authApi.endpoints.getOAuthStatus.useLazyQuery();
  const [unlink] = authApi.endpoints.unlinkOAuthAccount.useMutation();

  const handleOAuthWindowClosed = useCallback(() => {
    void fetchOAuthStatus({ clientKey: CLIENT_KEY })
      .unwrap()
      .then((status) => {
        if (status.linked && status.accessTokenValid) {
          toast.openToastWithMessage(
            t('toast:user.link_to_carbon_hub.success'),
          );
        }
      })
      .catch((error) => {
        toast.openToastWithError(t('toast:user.link_to_carbon_hub.error'));
        console.error(error);
      })
      .finally(() => {
        isLinkingInProgress.current = false;
      });
  }, [fetchOAuthStatus, t, toast]);

  const watchOAuthWindow = useCallback(
    (oauthWindow: Window | null) => {
      if (!oauthWindow) {
        return;
      }

      const pollingId = window.setInterval(() => {
        if (oauthWindow.closed) {
          window.clearInterval(pollingId);
          handleOAuthWindowClosed();
          return;
        }

        try {
          const popupUrl = oauthWindow.location.href;
          const hasOAuthResult =
            popupUrl.includes('/login-symfony') &&
            (popupUrl.includes('oauth_code=') ||
              popupUrl.includes('oauth_error='));

          if (hasOAuthResult) {
            oauthWindow.close();
          }
        } catch {
          // Cross-origin page: ignore until browser comes back to same origin.
        }
      }, 800);
    },
    [handleOAuthWindowClosed],
  );

  const linkToCarbonAccount = useCallback(async () => {
    if (isLinkingInProgress.current) {
      return;
    }

    isLinkingInProgress.current = true;

    try {
      const { data } = await SymfonyClient.get(`/auth/${CLIENT_KEY}/start-url`);
      const authorizationUrl = data.authorizationUrl;

      const oauthWindow = window.open(
        authorizationUrl,
        'carbon_hub_oauth',
        'popup=yes,width=600,height=700',
      );

      if (oauthWindow && !oauthWindow.closed) {
        watchOAuthWindow(oauthWindow);
        return;
      }

      isLinkingInProgress.current = false;
      window.location.assign(authorizationUrl);
    } catch (error) {
      toast.openToastWithError(t('toast:user.link_to_carbon_hub.error'));
      console.error(error);
      isLinkingInProgress.current = false;
    }
  }, [t, toast, watchOAuthWindow]);

  const unlinkFromCarbonHub = useCallback(() => {
    unlink({ clientKey: CLIENT_KEY })
      .unwrap()
      .then(() => {
        toast.openToastWithMessage(
          t('toast:user.unlink_from_carbon_hub.success'),
        );
      })
      .catch((error) => {
        toast.openToastWithError(t('toast:user.unlink_from_carbon_hub.error'));
        console.error(error);
      });
  }, [toast, t, unlink]);

  return {
    linkToCarbonAccount,
    unlinkFromCarbonHub,
    isLinkedToCarbonHub: OAuthStatus?.linked ?? false,
    isAccessTokenValid: OAuthStatus?.accessTokenValid ?? false,
  };
};
